import { RefObject, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { RemoteTrack, RemoteTrackPublication, Room, RoomEvent, Track } from 'livekit-client';
import { viewSupportScreenShareTemplate } from 'apollo/templates/support-screen-share';
import { SupportScreenShare, SupportScreenShareViewer } from 'models/support-screen-share';

export type ViewerStatus = 'CONNECTING' | 'WAITING_FOR_SCREEN' | 'WATCHING' | 'RECONNECTING' | 'ENDED' | 'ERROR';

const isScreen = (publication: RemoteTrackPublication) => publication.source === Track.Source.ScreenShare;

/**
 * Joins the customer's LiveKit room with a subscribe-only token and attaches the screen track to
 * the given video element. The token cannot publish or send data, so this is watch-only by design.
 */
export const useScreenShareViewer = (code: string, videoRef: RefObject<HTMLVideoElement>) => {
    const [status, setStatus] = useState<ViewerStatus>('CONNECTING');
    const [share, setShare] = useState<SupportScreenShare>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [viewSupportScreenShare] = useMutation<{ viewSupportScreenShare: SupportScreenShareViewer }>(
        viewSupportScreenShareTemplate,
    );

    useEffect(() => {
        const room = new Room({ adaptiveStream: false });
        let isCancelled = false;

        const attach = (track: RemoteTrack, publication: RemoteTrackPublication) => {
            if (isScreen(publication) && videoRef.current) {
                track.attach(videoRef.current);
                setStatus('WATCHING');
            }
        };
        room.on(RoomEvent.TrackSubscribed, (track, publication) => attach(track, publication))
            .on(RoomEvent.TrackUnsubscribed, (track, publication) => {
                track.detach();
                if (isScreen(publication)) setStatus('WAITING_FOR_SCREEN');
            })
            .on(RoomEvent.Reconnecting, () => setStatus('RECONNECTING'))
            .on(RoomEvent.Reconnected, () => setStatus('WATCHING'))
            .on(RoomEvent.Disconnected, () => {
                if (!isCancelled) setStatus('ENDED');
            });

        const connect = async () => {
            try {
                const { data } = await viewSupportScreenShare({ variables: { code } });
                if (!data || isCancelled) return;
                setShare(data.viewSupportScreenShare.share);
                await room.connect(data.viewSupportScreenShare.url, data.viewSupportScreenShare.accessToken);
                if (isCancelled) return;
                setStatus('WAITING_FOR_SCREEN');
                room.remoteParticipants.forEach((participant) =>
                    participant.trackPublications.forEach((publication) => {
                        if (publication.track) attach(publication.track, publication);
                    }),
                );
            } catch (error) {
                if (isCancelled) return;
                setErrorMessage((error as Error)?.message);
                setStatus('ERROR');
            }
        };
        connect();

        return () => {
            isCancelled = true;
            room.disconnect();
        };
    }, [code]);

    return { status, share, errorMessage };
};
